
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import BaseTree from '@/views/dashboard/_components/group-tree/base-tree.vue'
import { Group } from '@/views/dashboard/_type/group'
import { ServiceList, ServiceManageService } from '@/views/dashboard/group/client/service-manage/service-manage.service'
import { ElMessage, ElMessageBox } from 'element-plus'
import { formatTimeStamp } from '@/utils/time'
import { ServicePermissionService } from '@/services/service-permission.service'
import {
  SERVICE_BUY_ZH_TO_EN,
  SERVICE_BUY_ZH_TO_JA,
  SERVICE_BUY_ZH_TO_ZHTW,
} from '@/views/dashboard/service/service-subscribe/subscribe-buy-models-text-maps'

export default defineComponent({
  components: { BaseTree },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    deviceId: {
      type: String,
      default: ''
    },
    terminalType: {
      type: Number,
      default: undefined
    },
  },

  setup (props, context) {
    const { t, te, locale } = useI18n()
    const drawerTitle = computed(() => props.title || t('serviceManage.defaultTitle'))
    const injector = useReflectiveInjector([ServiceManageService])
    const service = injector.get(ServiceManageService)
    const servicePermissionService = injector.get(ServicePermissionService)

    const tableNode = ref()
    const isLoading = ref(false)
    const activeTab = ref('authorizedView')
    const tableData = ref<ServiceList[]>([])
    const currentGroup = ref({} as Group)
    const selectionData = ref<ServiceList[]>([])
    const selectionShot = ref<ServiceList[]>([])
    const isSelectAll = ref(false)
    const isShowSelection = ref(false)

    const searchParams = reactive({
      searchKey: '',
    })

    const pagination = reactive({
      currentPage: 1,
      pageSize: 9999,
      total: 0,
    })

    const isAuthorizedView = computed(() => {
      return activeTab.value === 'authorizedView'
    })

    /** 按当前语言选择中文→目标语言映射表（zh-CN 无需翻译） */
    const serviceTextMap = computed(() => {
      void locale.value
      switch (locale.value) {
        case 'en-US': return SERVICE_BUY_ZH_TO_EN
        case 'ja-JP': return SERVICE_BUY_ZH_TO_JA
        case 'zh-TW': return SERVICE_BUY_ZH_TO_ZHTW
        default: return null
      }
    })

    /** 将中文服务文本按当前语言翻译 */
    function translateZhText (zhText: string): string {
      if (!zhText) return ''
      const map = serviceTextMap.value
      return map?.[zhText] || zhText
    }

    /** price_id → i18n key 查找，回退到 buy_models 翻译映射，最终回退接口 display_name */
    function serviceDisplayName (item: ServiceList): string {
      void locale.value
      const id = item.price_id
      if (id != null) {
        const key = `mySubscribe.priceTitle.${id}`
        if (te(key)) return String(t(key))
      }
      return translateZhText(item.display_name || '')
    }

    /** class_name 按 buy_models 翻译映射做多语言展示 */
    function serviceClassName (item: ServiceList): string {
      void locale.value
      return translateZhText(item.class_name || '')
    }

    const selectedCount = computed(() => {
      if (isSelectAll.value) {
        return pagination.total
      }
      return selectionData.value.length
    })

    function handleClose () {
      context.emit('update:modelValue', false)
    }

    function updateData () {
      isLoading.value = true
      service.getServiceList({
        key_word: searchParams.searchKey,
        page: pagination.currentPage,
        limit: pagination.pageSize,
        is_auth: isAuthorizedView.value,
        device_id: props.deviceId,
        system_type: props.terminalType || undefined
      }).then((res) => {
        tableData.value = res.data
        pagination.total = +res.total || 0
        isLoading.value = false
        isSelectAll.value && doSelectAll()
      }).catch((error) => {
        isLoading.value = false
      })
    }

    function updateSelectionShow () {
      const { pageSize, currentPage } = pagination
      tableData.value = selectionShot.value.slice(pageSize * (currentPage - 1), pageSize * currentPage)
    }

    function terminalGroupSelected (row: Group) {
      currentGroup.value = row
      handleCancelSelect()
    }

    function handleSizeChange (value) {
      pagination.pageSize = value
      pagination.currentPage = 1
      if (isShowSelection.value && !isSelectAll.value) {
        updateSelectionShow()
      } else {
        updateData()
      }
    }

    function handleCurrentChange (value) {
      pagination.currentPage = value
      if (isShowSelection.value && !isSelectAll.value) {
        updateSelectionShow()
      } else {
        updateData()
      }
    }

    function doSelectAll () {
      tableData.value.forEach(row => tableNode.value?.toggleRowSelection(row))
    }

    function handleSearch () {
      pagination.currentPage = 1
      updateData()
    }

    function handleTabClick () {
      handleCancelSelect()
      updateData()
    }

    function handleSelectAll () {
      handleCancelSelect()
      isSelectAll.value = true
      doSelectAll()
    }

    function handleCancelSelect () {
      tableNode.value.clearSelection()
      isSelectAll.value = false
      toggleShowSelected(false)
    }

    function handleSelectionChange (values: ServiceList[]) {
      selectionData.value = values
    }

    /**
     * 切换展示已选数据
     * @param show 如果使用了show参数，则是切换到指定状态。不设置表示切换反状态
     */
    function toggleShowSelected (show?: boolean) {
      if (show !== isShowSelection.value) {
        pagination.currentPage = 1
      }
      isShowSelection.value = typeof show === 'boolean' ? show : !isShowSelection.value
      if (isShowSelection.value && !isSelectAll.value) {
        pagination.total = selectionData.value.length
        selectionShot.value = [...selectionData.value]
        updateSelectionShow()
      } else {
        updateData()
      }
    }

    function handleItemAuth (item: ServiceList) {
      ElMessageBox.confirm(isAuthorizedView.value ? t('serviceManage.confirmCancel') : t('serviceManage.confirmAdd'), t('serviceManage.dialogTip')).then(() => {
        servicePermissionService.authTerminal({
          permission_names: [item.permission_name],
          device_ids: [props.deviceId],
          is_auth: !isAuthorizedView.value
        }).then(() => {
          ElMessage.success(t('common.success'))
          updateData()
          context.emit('success')
        })
      })
    }

    function handleBatchOperate () {
      ElMessageBox.confirm(isAuthorizedView.value ? t('serviceManage.confirmCancel') : t('serviceManage.confirmAdd'), t('serviceManage.dialogTip')).then(() => {
        servicePermissionService.authTerminal({
          permission_names: isSelectAll.value ? undefined : selectionData.value.map(v => v.permission_name),
          device_ids: [props.deviceId],
          is_all_permission: isSelectAll.value ? true : undefined,
          is_auth: !isAuthorizedView.value
        }).then(() => {
          ElMessage.success(t('common.success'))
          handleCancelSelect()
          updateData()
          context.emit('success')
        })
      })
    }

    watch(() => props.modelValue, (value) => {
      if (value) {
        updateData()
      } else {
        activeTab.value = 'authorizedView'
        searchParams.searchKey = ''
        handleCancelSelect()
      }
    })

    return {
      t,
      drawerTitle,
      activeTab,
      tableNode,
      tableData,
      isLoading,
      searchParams,
      pagination,
      isSelectAll,
      isShowSelection,
      selectionData,
      selectedCount,
      isAuthorizedView,
      formatTimeStamp,
      updateData,
      handleTabClick,
      terminalGroupSelected,
      handleSizeChange,
      handleCurrentChange,
      handleSearch,
      handleSelectAll,
      handleCancelSelect,
      handleSelectionChange,
      toggleShowSelected,
      handleClose,
      handleItemAuth,
      handleBatchOperate,
      serviceDisplayName,
      serviceClassName,
    }
  }
})
